import { useEffect, useState } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Button,
  Table,
  Switch,
  Space,
  message,
  Spin,
  Tooltip,
  Typography,
  Alert,
} from 'antd';
import { SaveOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';
import { t, getLocale } from '../../i18n';

const { Title, Paragraph } = Typography;

type ActivityScoreReason =
  | 'TRIP_COMPLETED'
  | 'FIVE_STAR_BONUS'
  | 'FOUR_STAR_BONUS'
  | 'LOW_RATING_2_STAR'
  | 'LOW_RATING_1_STAR'
  | 'DRIVER_CANCELLED'
  | 'DRIVER_REJECTED'
  | 'DRIVER_NO_SHOW'
  | 'ADMIN_ADJUSTMENT'
  | 'BLOCK_RECOVERY_RESET'
  | 'AUTO_BLOCKED';

interface Rule {
  reason: ActivityScoreReason;
  points: number;
  isActive: boolean;
  updatedAt: string;
}

interface Config {
  blockThreshold: number;
  unblockReset: number;
  defaultScore: number;
  updatedAt: string;
}

interface ApiResponse {
  rules: Rule[];
  config: Config;
}

const SYSTEM_MANAGED: ActivityScoreReason[] = [
  'ADMIN_ADJUSTMENT',
  'BLOCK_RECOVERY_RESET',
  'AUTO_BLOCKED',
];

// Hardcoded in the backend ActivityScoreReason enum — mirrored here for UI labels.
const REASON_LABELS: Record<
  ActivityScoreReason,
  { uz: string; ru: string; hint?: { uz: string; ru: string } }
> = {
  TRIP_COMPLETED: {
    uz: 'Safar yakunlandi',
    ru: 'Поездка завершена',
  },
  FIVE_STAR_BONUS: {
    uz: "5 yulduzli baholash bonusi",
    ru: 'Бонус за 5 звёзд',
  },
  FOUR_STAR_BONUS: {
    uz: '4 yulduzli baholash bonusi',
    ru: 'Бонус за 4 звезды',
  },
  LOW_RATING_2_STAR: {
    uz: '2 yulduzli past baho',
    ru: 'Низкая оценка 2 звезды',
  },
  LOW_RATING_1_STAR: {
    uz: '1 yulduzli past baho',
    ru: 'Низкая оценка 1 звезда',
  },
  DRIVER_CANCELLED: {
    uz: 'Haydovchi safarni bekor qildi',
    ru: 'Водитель отменил поездку',
  },
  DRIVER_REJECTED: {
    uz: 'Haydovchi taklifni rad etdi',
    ru: 'Водитель отклонил предложение',
  },
  DRIVER_NO_SHOW: {
    uz: 'Haydovchi yetib kelmadi',
    ru: 'Водитель не приехал',
  },
  ADMIN_ADJUSTMENT: {
    uz: 'Administrator tahriri',
    ru: 'Корректировка администратором',
    hint: {
      uz: 'Ball administrator tomonidan belgilanadi. Bu qator tizim tomonidan boshqariladi.',
      ru: 'Балл задаётся администратором вручную. Строка управляется системой.',
    },
  },
  BLOCK_RECOVERY_RESET: {
    uz: 'Blokdan chiqarishda tiklash',
    ru: 'Сброс при разблокировке',
    hint: {
      uz: 'Ball avtomatik hisoblanadi (chegara va reset qiymatlari orqali).',
      ru: 'Балл вычисляется автоматически (из порога и значения сброса).',
    },
  },
  AUTO_BLOCKED: {
    uz: 'Avtomatik bloklangan',
    ru: 'Автоматическая блокировка',
    hint: {
      uz: 'Faqat tarixga yoziladi — ball o\'zgartirilmaydi.',
      ru: 'Только отметка в истории — балл не меняется.',
    },
  },
};

const labelFor = (reason: ActivityScoreReason): string => {
  const entry = REASON_LABELS[reason];
  return getLocale() === 'ru' ? entry.ru : entry.uz;
};

const hintFor = (reason: ActivityScoreReason): string | null => {
  const entry = REASON_LABELS[reason];
  if (!entry.hint) return null;
  return getLocale() === 'ru' ? entry.hint.ru : entry.hint.uz;
};

export const FaollikBaliPage = () => {
  const { can } = usePermission();
  const canUpdate = can('settings', 'update');

  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<Rule[]>([]);
  const [savingReasons, setSavingReasons] = useState<Set<string>>(new Set());
  const [savingConfig, setSavingConfig] = useState(false);
  const [configForm] = Form.useForm<Config>();

  // Local edits to rule points keyed by reason. Admin can tweak before hitting Save.
  const [ruleEdits, setRuleEdits] = useState<Record<string, { points?: number; isActive?: boolean }>>(
    {}
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ApiResponse>('/admin/faollik-bali');
      setRules(data.rules);
      configForm.setFieldsValue(data.config);
      setRuleEdits({});
    } catch {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRule = async (reason: ActivityScoreReason) => {
    const edits = ruleEdits[reason];
    if (!edits || (edits.points === undefined && edits.isActive === undefined)) {
      message.info(getLocale() === 'ru' ? 'Нет изменений' : "O'zgarishlar yo'q");
      return;
    }

    setSavingReasons((s) => new Set(s).add(reason));
    try {
      await apiClient.put(`/admin/faollik-bali/rules/${reason}`, edits);
      message.success(t('common.saved') || 'Saqlandi');
      await loadData();
    } catch {
      message.error(t('common.error'));
    } finally {
      setSavingReasons((s) => {
        const next = new Set(s);
        next.delete(reason);
        return next;
      });
    }
  };

  const saveConfig = async () => {
    try {
      const values = await configForm.validateFields();
      setSavingConfig(true);
      await apiClient.put('/admin/faollik-bali/config', values);
      message.success(t('common.saved') || 'Saqlandi');
      await loadData();
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return; // validation error
      message.error(t('common.error'));
    } finally {
      setSavingConfig(false);
    }
  };

  const columns = [
    {
      title: getLocale() === 'ru' ? 'Причина' : 'Sabab',
      key: 'reason',
      render: (_: unknown, rec: Rule) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{labelFor(rec.reason)}</span>
          <span style={{ color: '#999', fontSize: 12, fontFamily: 'monospace' }}>
            {rec.reason}
          </span>
        </Space>
      ),
    },
    {
      title: getLocale() === 'ru' ? 'Баллы' : 'Ballar',
      key: 'points',
      width: 180,
      render: (_: unknown, rec: Rule) => {
        const isSystem = SYSTEM_MANAGED.includes(rec.reason);
        const editedValue = ruleEdits[rec.reason]?.points;
        const value = editedValue ?? rec.points;

        if (isSystem) {
          return (
            <Tooltip title={hintFor(rec.reason) ?? ''}>
              <Space>
                <InputNumber value={value} disabled style={{ width: 100 }} />
                <InfoCircleOutlined style={{ color: '#999' }} />
              </Space>
            </Tooltip>
          );
        }

        return (
          <InputNumber
            value={value}
            step={0.1}
            precision={2}
            min={-100}
            max={100}
            disabled={!canUpdate}
            onChange={(v) =>
              setRuleEdits((prev) => ({
                ...prev,
                [rec.reason]: { ...prev[rec.reason], points: Number(v) },
              }))
            }
            style={{ width: 120 }}
          />
        );
      },
    },
    {
      title: getLocale() === 'ru' ? 'Активно' : 'Faol',
      key: 'isActive',
      width: 100,
      render: (_: unknown, rec: Rule) => {
        const isSystem = SYSTEM_MANAGED.includes(rec.reason);
        const edited = ruleEdits[rec.reason]?.isActive;
        const value = edited ?? rec.isActive;
        return (
          <Switch
            checked={value}
            disabled={isSystem || !canUpdate}
            onChange={(v) =>
              setRuleEdits((prev) => ({
                ...prev,
                [rec.reason]: { ...prev[rec.reason], isActive: v },
              }))
            }
          />
        );
      },
    },
    {
      title: getLocale() === 'ru' ? 'Действия' : 'Amallar',
      key: 'actions',
      width: 120,
      render: (_: unknown, rec: Rule) => {
        const isSystem = SYSTEM_MANAGED.includes(rec.reason);
        const edits = ruleEdits[rec.reason];
        const hasEdits = edits && (edits.points !== undefined || edits.isActive !== undefined);
        return (
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            loading={savingReasons.has(rec.reason)}
            disabled={isSystem || !canUpdate || !hasEdits}
            onClick={() => saveRule(rec.reason)}
          >
            {getLocale() === 'ru' ? 'Сохранить' : 'Saqlash'}
          </Button>
        );
      },
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 24, maxWidth: 960 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          {t('nav.faollikBali')}
        </Title>
        <Paragraph type="secondary">
          {getLocale() === 'ru'
            ? 'Управление порогами блокировки и величиной начислений/списаний баллов активности водителей.'
            : 'Haydovchilarning faollik bali chegaralari va har bir sabab uchun qo\'shiladigan / ayriladigan ballarni boshqarish.'}
        </Paragraph>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            getLocale() === 'ru'
              ? 'Список причин захардкожен в коде. Редактируются только величины баллов и флаг активности.'
              : "Sabablar ro'yxati kodda qattiq belgilangan. Faqat ballar va faollik bayroqlari tahrirlanadi."
          }
        />

        <Card
          title={
            getLocale() === 'ru' ? 'Пороговые значения' : 'Chegara qiymatlari'
          }
          style={{ marginBottom: 24 }}
          extra={
            canUpdate && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingConfig}
                onClick={saveConfig}
              >
                {getLocale() === 'ru' ? 'Сохранить' : 'Saqlash'}
              </Button>
            )
          }
        >
          <Form form={configForm} layout="vertical" disabled={!canUpdate}>
            <Space size="large" wrap>
              <Form.Item
                name="blockThreshold"
                label={
                  getLocale() === 'ru'
                    ? 'Порог блокировки (балл ниже — блок)'
                    : 'Blok chegarasi (bal undan past bo\'lsa — blok)'
                }
                rules={[{ required: true }]}
              >
                <InputNumber min={0} max={100} style={{ width: 140 }} />
              </Form.Item>
              <Form.Item
                name="unblockReset"
                label={
                  getLocale() === 'ru'
                    ? 'Балл после разблокировки'
                    : 'Blokdan chiqarishdan keyingi bal'
                }
                rules={[{ required: true }]}
              >
                <InputNumber min={0} max={100} style={{ width: 140 }} />
              </Form.Item>
              <Form.Item
                name="defaultScore"
                label={
                  getLocale() === 'ru'
                    ? 'Стартовый балл новых водителей'
                    : 'Yangi haydovchilarning boshlang\'ich bali'
                }
                rules={[{ required: true }]}
              >
                <InputNumber min={0} max={100} style={{ width: 140 }} />
              </Form.Item>
            </Space>
          </Form>
        </Card>

        <Card title={getLocale() === 'ru' ? 'Правила' : 'Qoidalar'}>
          <Table
            rowKey="reason"
            dataSource={rules}
            columns={columns}
            pagination={false}
            size="middle"
          />
        </Card>
      </div>
    </Spin>
  );
};
